/**
 * Exports contract (F20, roadmap Phase 1): the export routes, the list of
 * exports a reader may download, the PRISMA 2020 counts and the data the web
 * app turns into the Word report. Re-exported from index.ts.
 *
 * Owner: audit and reporting (F20). Shapes follow
 * specs/features/F20-audit-reporting/design.md, "Phase 1 exports".
 */
import type { EvidencePage } from '@bioterafi/evidence-model';
import { pagePath } from './paths';

/**
 * Kinds served by `GET .../exports/{kind}` (E49, E181, E182). `methods`, `protocol` and `package`
 * are specified but not delivered yet, so they answer 404 like an unknown kind (RPT-140).
 */
export type ExportKind = 'csv' | 'data' | 'revman' | 'r-script' | 'prisma' | 'report';

/** Tables of the CSV export (`csv?part=`; RPT-020, RPT-027, RPT-150 to RPT-152). */
export type CsvPart = 'outcomes' | 'studies' | 'arms' | 'dictionary';

export const EXPORT_ROUTES = {
  /** GET -> 200 ExportListResponse: what this reader may download (E180; RPT-120, RPT-121). */
  exportList: (slug: string) => `${pagePath(slug)}/exports`,
  /** GET -> 200 the file (E49, E181, E182; RPT-010). */
  exportFile: (slug: string, kind: ExportKind, query?: Record<string, string>) => {
    const qs = query && Object.keys(query).length ? `?${new URLSearchParams(query).toString()}` : '';
    return `${pagePath(slug)}/exports/${kind}${qs}`;
  },
  /** GET ?format=csv|json -> 200 the decision log, members only (E50; RPT-080, RPT-141). */
  decisionLog: (slug: string, format: 'csv' | 'json' = 'csv') => `${pagePath(slug)}/decision-log?format=${format}`,
} as const;

/** Why an export cannot be downloaded now (RPT-121). */
export type ExportUnavailableReason = 'no_run_logs' | 'members_only' | 'no_analysable_studies';

/** One downloadable item on the exports screen (RPT-120). */
export interface ExportItem {
  /** Stable key: the kind, or kind plus part or format, e.g. 'csv:studies', 'prisma:svg'. */
  id: string;
  kind: ExportKind | 'decision-log';
  /** Query to append to the export route; empty for none. */
  query: Record<string, string>;
  available: boolean;
  reason: ExportUnavailableReason | null;
}

/** GET EXPORT_ROUTES.exportList (E180). */
export interface ExportListResponse {
  slug: string;
  /** True when the reader is a member of the page's workspace (any role). */
  member: boolean;
  items: ExportItem[];
}

/** One source in a PRISMA identification box. `n` is null when the search failed (never shown as zero). */
export interface PrismaSourceBox {
  name: string;
  n: number | null;
  status: 'ok' | 'partial' | 'failed';
}

/** One full-text exclusion reason, with the frozen rule text (RPT-064). */
export interface PrismaExclusionReason {
  ruleId: string;
  text: string;
  n: number;
}

/**
 * PRISMA 2020 counts for the new-review template (RPT-060 to RPT-066, RPT-070), over every
 * completed run of the page. Built from the stage-owned parts (F13 identification, F14
 * deduplication, F15 screening) and the included studies.
 */
export interface PrismaCounts {
  template: 'new';
  scope: 'cumulative';
  /** Completed scheduler or manual runs counted, oldest first. */
  runIds: string[];
  identification: {
    databases: { total: number; sources: PrismaSourceBox[] };
    registers: { total: number; sources: PrismaSourceBox[] };
    removedBeforeScreening: { duplicates: number; automation: 0; otherReasons: number };
  };
  screening: { screened: number; excluded: { total: number; byModels: number; byReviewer: number }; awaiting: number };
  retrieval: { sought: number; notRetrieved: number; awaiting: number };
  eligibility: { assessed: number; awaiting: number; excluded: { total: number; reasons: PrismaExclusionReason[] } };
  otherMethods: {
    citationSearching: number | null;
    sources: PrismaSourceBox[];
    identified: number;
    screened: number;
    excludedTitleAbstract: number;
    awaitingScreening: number;
    sought: number;
    notRetrieved: number;
    awaitingRetrieval: number;
    assessed: number;
    awaiting: number;
    excluded: { total: number; reasons: PrismaExclusionReason[] };
  };
  included: { studies: number; reports: number; inPrimaryAnalysis: number };
  /** RPT-069, in order. */
  notes: string[];
}

/** One search strategy as approved, for the report's search section (RPT-171). */
export interface ReportSearch {
  source: string;
  /** Human name of the source, e.g. "PubMed". */
  sourceName: string;
  version: number;
  queryText: string;
  approvedAt: string | null;
}

/** One search execution of a completed run (RPT-171). */
export interface ReportSearchExecution {
  runId: string;
  sourceName: string;
  kind: 'search' | 'upload' | 'citation_backward' | 'citation_forward';
  executedAt: string;
  dateFrom: string | null;
  dateTo: string | null;
  hitCount: number | null;
  retrieved: number;
  status: 'running' | 'ok' | 'partial' | 'failed';
}

/**
 * Which review steps used AI, with the provider and model ID (RPT-174). Model IDs appear only in
 * exports meant for journal disclosure, never in the web app's own text (CONT-058).
 */
export interface ReportAiUse {
  /** Registry purpose, e.g. 'screening_pass_a'. */
  purpose: string;
  provider: string;
  model: string;
  promptVersion: string | null;
  calls: number;
  firstAt: string;
  lastAt: string;
}

/** GET exports/report (E182): what the Word report is built from, members only (RPT-170). */
export interface ReportDataResponse {
  /** The page as stored, machine values included; the report itself uses confirmed values only (RPT-173). */
  page: EvidencePage;
  evidenceTableHash: string;
  /** SHA-256 of the page protocol (EVM-088), or null when not frozen. */
  protocolHash: string | null;
  /** The question's protocol document hash (QB-084), when the page came from a question. */
  questionProtocolHash: string | null;
  engineVersion: string;
  prisma: PrismaCounts | null;
  searches: ReportSearch[];
  executions: ReportSearchExecution[];
  aiUse: ReportAiUse[];
  /** Screening decisions recorded on the page, by stage and actor kind. */
  screening: { titleAbstractDecisions: number; fullTextDecisions: number; humanDecisions: number; modelDecisions: number };
}
